/**
 * Workspace widget grid: 12 columns × 32 rows fitted to the left zone
 * (design handoff-10 `Workspace.dc.html`). Pure functions only — the page
 * keeps the state and calls these on every move, resize, collapse, add and
 * remove.
 */

export const WIDGET_IDS = ['search', 'activity', 'feed', 'active', 'pinned'] as const;
export type WidgetId = (typeof WIDGET_IDS)[number];

export interface Cell {
  x: number;
  y: number;
  w: number;
  h: number;
}
export type Layout = Record<WidgetId, Cell>;
export type Flags = Partial<Record<WidgetId, boolean>>;

/** A collapsed widget keeps its header only: two rows. */
const COLLAPSED_ROWS = 2;

function rows(id: WidgetId, cell: Cell, collapsed: Flags): number {
  return collapsed[id] ? COLLAPSED_ROWS : cell.h;
}

/**
 * Settle the board: every visible widget floats up as far as it can without
 * overlapping another. When `moved` is given that widget keeps its spot and
 * the others are pushed down out of its way first.
 */
export function compact(layout: Layout, hidden: Flags, collapsed: Flags, moved?: WidgetId): Layout {
  type Item = Cell & { id: WidgetId };
  const height = (it: Item) => rows(it.id, it, collapsed);
  const hit = (a: Item, b: Item) =>
    a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + height(b) && b.y < a.y + height(a);

  let items: Item[] = WIDGET_IDS.filter((id) => !hidden[id]).map((id) => ({ id, ...layout[id] }));
  if (moved) {
    const anchor = items.find((it) => it.id === moved);
    if (anchor) {
      const rest = items.filter((it) => it !== anchor).sort((a, b) => a.y - b.y || a.x - b.x);
      const placed: Item[] = [anchor];
      for (const it of rest) {
        while (placed.some((p) => hit(it, p))) it.y++;
        placed.push(it);
      }
      items = placed;
    }
  }
  items.sort((a, b) => a.y - b.y || a.x - b.x || (a.id === moved ? -1 : b.id === moved ? 1 : 0));

  const done: Item[] = [];
  for (const it of items) {
    while (it.y > 0 && !done.some((p) => hit({ ...it, y: it.y - 1 }, p))) it.y--;
    while (done.some((p) => hit(it, p))) it.y++;
    done.push(it);
  }

  const out = { ...layout };
  for (const { id, x, y, w, h } of done) out[id] = { x, y, w, h };
  return out;
}

export const COLUMNS = 12;

/** Drop `id` at grid cell `to` (clamped into the board) and settle the rest around it. */
export function moveWidget(layout: Layout, id: WidgetId, to: { x: number; y: number }, hidden: Flags, collapsed: Flags): Layout {
  const cell = layout[id];
  const x = Math.min(Math.max(0, Math.round(to.x)), COLUMNS - cell.w);
  const y = Math.max(0, Math.round(to.y));
  return compact({ ...layout, [id]: { ...cell, x, y } }, hidden, collapsed, id);
}

/** Smallest size per widget; `fixedH` pins the height (Search is one strip). */
export const MIN_SIZE: Record<WidgetId, { w: number; h: number; fixedH?: number }> = {
  search: { w: 4, h: 2, fixedH: 2 },
  activity: { w: 4, h: 10 },
  feed: { w: 4, h: 10 },
  active: { w: 4, h: 8 },
  pinned: { w: 4, h: 4 },
};

/** Resize `id` to `size` (clamped to its minimum and the right edge) and settle the rest. */
export function resizeWidget(layout: Layout, id: WidgetId, size: { w: number; h: number }, hidden: Flags, collapsed: Flags): Layout {
  const cell = layout[id];
  const min = MIN_SIZE[id];
  const w = Math.min(Math.max(min.w, Math.round(size.w)), COLUMNS - cell.x);
  const h = min.fixedH ?? Math.max(min.h, Math.round(size.h));
  if (w === cell.w && h === cell.h) return layout;
  return compact({ ...layout, [id]: { ...cell, w, h } }, hidden, collapsed, id);
}

/** Rows the board is fitted to; the zone scrolls past them only when widgets do. */
export const ROWS = 32;

/** Default board (design Workspace.dc.html): Search and Pinned across, then Activity | Feed | Active chats. */
export const DEFAULT_LAYOUT: Layout = {
  search: { x: 0, y: 0, w: 12, h: 2 },
  pinned: { x: 0, y: 2, w: 12, h: 6 },
  activity: { x: 0, y: 8, w: 4, h: 24 },
  feed: { x: 4, y: 8, w: 4, h: 24 },
  active: { x: 8, y: 8, w: 4, h: 24 },
};
export const DEFAULT_HIDDEN: Flags = {};

/** Bring a removed widget back under everything else, at its default size. */
export function addWidget(layout: Layout, id: WidgetId, hidden: Flags, collapsed: Flags): { layout: Layout; hidden: Flags } {
  const nextHidden = { ...hidden };
  delete nextHidden[id];
  const bottom = Math.max(
    0,
    ...WIDGET_IDS.filter((k) => !nextHidden[k] && k !== id).map((k) => layout[k].y + rows(k, layout[k], collapsed)),
  );
  const next = { ...layout, [id]: { ...DEFAULT_LAYOUT[id], x: 0, y: bottom } };
  return { layout: compact(next, nextHidden, collapsed), hidden: nextHidden };
}

export interface Geometry {
  pad: number;
  gap: number;
  colW: number;
  rowH: number;
}

/** Column and row size for a zone of `width` × `height` px. */
export function geometry(width: number, height: number): Geometry {
  const pad = 12;
  const gap = 12;
  return {
    pad,
    gap,
    colW: Math.max(10, (width - 2 * pad - (COLUMNS - 1) * gap) / COLUMNS),
    rowH: Math.max(18, (height - 2 * pad - (ROWS - 1) * gap) / ROWS),
  };
}

/** Pixel box of a cell `rowsTall` rows high (a collapsed widget passes 2). */
export function cellRect(cell: Cell, rowsTall: number, g: Geometry) {
  return {
    left: g.pad + cell.x * (g.colW + g.gap),
    top: g.pad + cell.y * (g.rowH + g.gap),
    width: cell.w * g.colW + (cell.w - 1) * g.gap,
    height: rowsTall * g.rowH + (rowsTall - 1) * g.gap,
  };
}

/** Pinned tiles need 168 px; Pinned gets that and the columns below fill the board. */
const PINNED_MIN_PX = 168;

/** The default board fitted to the zone (used until the person edits the layout). */
export function fittedLayout(g: Geometry): Layout {
  const pinnedRows = Math.max(MIN_SIZE.pinned.h, Math.ceil((PINNED_MIN_PX + g.gap) / (g.rowH + g.gap)));
  const y = DEFAULT_LAYOUT.search.h + pinnedRows;
  const h = ROWS - y;
  return {
    ...DEFAULT_LAYOUT,
    pinned: { ...DEFAULT_LAYOUT.pinned, h: pinnedRows },
    activity: { ...DEFAULT_LAYOUT.activity, y, h },
    feed: { ...DEFAULT_LAYOUT.feed, y, h },
    active: { ...DEFAULT_LAYOUT.active, y, h },
  };
}
